import { useEffect, useState, useMemo, useRef } from "react";
import { AnalyticsService } from "@/modules/analytics/services/analytics.service";
import { GoogleAnalyticsService } from "@/modules/analytics/services/providers/google-analytics.provider.service";
import { MixpanelAnalyticsService } from "@/modules/analytics/services/providers/mixpanel.provider.service";
import { MoEngageAnalyticsService } from "@/modules/analytics/services/providers/moengage.provider.service";
import { AnalyticsServiceProviders } from "@/modules/analytics/types";
import {
  EnvironmentVariables,
  OrganizationTierTypes,
} from "@/modules/common/types";
import useAuth from "@/modules/common/hooks/auth.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { DecodedToken } from "@/modules/common/context/auth/auth.context";
import { useUserAvailability } from "@/modules/common/hooks/user-availability.hook";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";

const analyticsServices = [
  {
    provider: AnalyticsServiceProviders.GOOGLE,
    service: new GoogleAnalyticsService(),
  },
  {
    provider: AnalyticsServiceProviders.MOENGAGE,
    service: new MoEngageAnalyticsService(),
  },
  {
    provider: AnalyticsServiceProviders.MIXPANEL,
    service: new MixpanelAnalyticsService(),
  },
];

export function useAnalytics() {
  const [ready, setReady] = useState(false);

  const screen = useScreenSize();
  const { user } = useAuth();
  const hasUser = Boolean(user);

  const {
    selectedOrganization,
    managedOrganizations,
    selectedSingleOrganization,
    selectedOrganizations,
    filteredManagedOrganizations,
    showSingleOrganizationTables,
    showMultipleOrganizationTables,
    renderOrganisationsActions,
  } = useOrganization();

  const isOrgApplied =
    showSingleOrganizationTables || showMultipleOrganizationTables;

  // When no dropdown is visible (e.g. Nawy), use the auto-set selectedOrganization from the provider.
  // When a dropdown is visible, resolve from the user's explicit selection:
  //   1. selectedOrganizations[0]   — first org selected from the / multi-org dropdown
  //   2. selectedSingleOrganization — selected from the /leads single-org dropdown
  // NOTE: selectedSingleOrganization is second because it currently appears to have no effect (BUG?)
  const orgForAnalytics = !renderOrganisationsActions
    ? selectedOrganization
    : [selectedOrganizations?.[0], selectedSingleOrganization].find(
        (org) => org?.uid,
      );

  const { availabilityData } = useUserAvailability({ enabled: hasUser });
  const userAvailability = hasUser ? availabilityData?.data?.user : null;

  const userInfo = useMemo(() => {
    if (!userAvailability) return null;

    const { name, rankId, rank } = userAvailability;
    const { title, value } = rank ?? {};
    const hasValidRank = Boolean(title && typeof value === "number");

    return {
      name: name ?? null,
      ...(hasValidRank && {
        rank: { rankId: rankId!, rankTitle: title!, rankValue: value! },
      }),
    };
  }, [userAvailability]);

  const stableUserForIdentify = useMemo(() => {
    if (!hasUser) return null;

    return {
      sub: user?.sub || null,
      name: user?.name || null,
      email: user?.email || null,
      phone_number: user?.phone_number || null,
      "cognito:groups": user?.["cognito:groups"] || [],
      "cognito:username": user?.["cognito:username"] || null,
    };
  }, [hasUser, user]);

  const analytics = useMemo(() => {
    if (
      !hasUser ||
      !stableUserForIdentify ||
      !EnvironmentVariables.COMPANY_NAME
    )
      return null;

    const analyticsService = new AnalyticsService(
      EnvironmentVariables.COMPANY_NAME.toString() as OrganizationTierTypes,
      () => ({
        device: screen.isMobile ? "mobile" : "desktop",
        userUid: stableUserForIdentify?.["cognito:username"] ?? null,
      }),
    );

    analyticsServices.forEach(({ provider, service }) =>
      analyticsService.register(provider, service),
    );

    return analyticsService;
  }, [hasUser, stableUserForIdentify, screen.isMobile]);

  const initializedRef = useRef(false);
  const identifiedUserRef = useRef<string | null>(null);

  // ✅ Initialize and identify user
  useEffect(() => {
    let mounted = true;

    if (!analytics || initializedRef.current) return;

    analytics
      .initAll()
      .then(() => {
        if (!mounted) return;
        initializedRef.current = true;
        setReady(true);

        const stableId =
          stableUserForIdentify?.["cognito:username"] ||
          stableUserForIdentify?.sub;

        if (stableId && identifiedUserRef.current !== stableId) {
          identifiedUserRef.current = stableId;
          analytics.identify(
            stableUserForIdentify as DecodedToken,
            AnalyticsServiceProviders.ALL,
          );
        }
      })
      .catch((err) => {
        console.error("Analytics init failed", err);
      });

    return () => {
      mounted = false;
    };
  }, [analytics, stableUserForIdentify]);

  useEffect(() => {
    if (!ready || !analytics || !userInfo?.rank) return;

    analytics.setUserAttributes(
      { rank: userInfo.rank, $name: userInfo.name },
      AnalyticsServiceProviders.MIXPANEL,
    );
  }, [ready, analytics, userInfo]);

  useEffect(() => {
    if (
      !ready ||
      !analytics ||
      !orgForAnalytics?.name ||
      !filteredManagedOrganizations?.length
    )
      return;
    if (renderOrganisationsActions && !isOrgApplied) return;

    analytics.setUserAttributes(
      {
        organization_name: orgForAnalytics.name,
        has_multiple_organizations: (managedOrganizations?.length ?? 0) > 1,
      },
      AnalyticsServiceProviders.MIXPANEL,
    );
  }, [
    ready,
    analytics,
    isOrgApplied,
    orgForAnalytics,
    managedOrganizations,
    renderOrganisationsActions,
    filteredManagedOrganizations,
  ]);

  return { analytics, ready, userInfo };
}
